import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Measures an element's height (for sizing the virtualized list to fill the
 * remaining viewport). Uses ResizeObserver.
 */
export function useElementSize<T extends HTMLElement>(): {
  ref: (node: T | null) => void;
  height: number;
} {
  const [height, setHeight] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    if (!node) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setHeight(entry.contentRect.height);
    });
    ro.observe(node);
    observerRef.current = ro;
    setHeight(node.getBoundingClientRect().height);
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { ref, height };
}
